import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconLock,
    IconLockOpen,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { getPdfEncryptionInfo, lockPdf, unlockPdf } from './protectPdfEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import SegmentedTabs from '../../image/shared/SegmentedTabs';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

export default function ProtectPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [encrypted, setEncrypted] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [mode, setMode] = useState('lock');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [allowPrint, setAllowPrint] = useState(true);
    const [allowCopy, setAllowCopy] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const resetSecrets = () => {
        setPassword('');
        setConfirmPassword('');
        setResult(null);
    };

    const loadFile = useCallback(async (incoming) => {
        setError(null);
        if (!incoming) return;
        if (!(incoming.type === 'application/pdf' || incoming.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('protectPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_FILE_BYTES) {
            const msg = t('protectPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setLoadingMeta(true);
        setFile(incoming);
        resetSecrets();
        try {
            const info = await getPdfEncryptionInfo(incoming);
            setEncrypted(!!info.encrypted);
            setMode(info.encrypted ? 'unlock' : 'lock');
        } catch {
            setFile(null);
            setEncrypted(null);
            const msg = t('protectPdf.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setLoadingMeta(false);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in. This tool
    // holds one file, so a paste of several takes the first.
    usePdfPaste((files) => loadFile(files[0]));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) loadFile(dropped);
    };

    const clearFile = () => {
        setFile(null);
        setEncrypted(null);
        setPassword('');
        setConfirmPassword('');
        setResult(null);
        setError(null);
    };

    const handleEditAgain = () => {
        setResult(null);
        setError(null);
    };

    const handleSubmit = async () => {
        if (!file || processing) return;

        if (mode === 'lock') {
            if (!password) {
                const msg = t('protectPdf.errors.emptyPassword');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                return;
            }
            if (password !== confirmPassword) {
                const msg = t('protectPdf.errors.mismatch');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                return;
            }
        } else if (!password) {
            const msg = t('protectPdf.errors.emptyPassword');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('pdf/protect', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const base = file.name.replace(/\.pdf$/i, '');
            const blob = mode === 'lock'
                ? await lockPdf(file, password, { allowPrint, allowCopy })
                : await unlockPdf(file, password);

            setResult({
                name: mode === 'lock' ? `${base}-locked.pdf` : `${base}-unlocked.pdf`,
                blob,
            });
            notifications.show({ title: t('common.success'), message: t('protectPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('protectPdf.errors.failed');
            if (err?.message === 'alreadyEncrypted') msg = t('protectPdf.errors.alreadyEncrypted');
            else if (err?.message === 'notEncrypted') msg = t('protectPdf.errors.notEncrypted');
            else if (err?.message === 'wrongPassword') msg = t('protectPdf.errors.wrongPassword');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const canSubmit = file && !loadingMeta && !processing
        && !(mode === 'lock' && encrypted === true)
        && !(mode === 'unlock' && encrypted === false)
        && (mode === 'unlock' ? password : password && confirmPassword);

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!canSubmit,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        success: t('protectPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const fileMeta = file
        ? [
            formatFileSize(file.size),
            loadingMeta ? t('protectPdf.reading') : null,
            !loadingMeta && encrypted != null ? (encrypted ? t('protectPdf.statusLocked') : t('protectPdf.statusOpen')) : null,
        ].filter(Boolean).join(' · ')
        : '';
    const resultMeta = result ? formatFileSize(result.blob.size) : undefined;

    return (
        <ToolPageLayout
            slug="pdf/protect"
            subtitleKey="protectPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file && !loadingMeta}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? clearFile : undefined}
            primaryAction={{
                label: mode === 'lock' ? t('protectPdf.lock') : t('protectPdf.unlock'),
                onClick: handleSubmit,
                disabled: !canSubmit || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('protectPdf.dropTitle')}
                            hint={t('protectPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <PdfUploadedFile
                            name={file.name}
                            meta={fileMeta}
                            onRemove={clearFile}
                            removeLabel={t('protectPdf.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file && !loadingMeta ? (
                <ToolCard title={t('protectPdf.modeTitle')}>
                    <div className="space-y-4">
                        {/* Icon over the name, with the app's tooltip. A mode that cannot apply to this
                            file (already locked / not locked) stays visible but off, and says why. */}
                        <SegmentedTabs
                            ariaLabel={t('protectPdf.modeTitle')}
                            stacked
                            value={mode}
                            onChange={(next) => { setMode(next); setError(null); resetSecrets(); }}
                            options={[
                                {
                                    id: 'lock',
                                    label: t('protectPdf.modeLock'),
                                    icon: IconLock,
                                    disabled: encrypted === true,
                                    hint: encrypted === true ? t('protectPdf.alreadyEncryptedHint') : t('protectPdf.modeLockHint'),
                                },
                                {
                                    id: 'unlock',
                                    label: t('protectPdf.modeUnlock'),
                                    icon: IconLockOpen,
                                    disabled: encrypted === false,
                                    hint: encrypted === false ? t('protectPdf.notEncryptedHint') : t('protectPdf.modeUnlockHint'),
                                },
                            ]}
                        />
                        <div className="space-y-1.5">
                            <label htmlFor="protect-password" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                {mode === 'lock' ? t('protectPdf.passwordLabel') : t('protectPdf.currentPasswordLabel')}
                            </label>
                            <input
                                id="protect-password"
                                type="password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setResult(null); setError(null); }}
                                placeholder={t('protectPdf.passwordPlaceholder')}
                                className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                            />
                        </div>

                        {mode === 'lock' && (
                            <>
                                <div className="space-y-1.5">
                                    <label htmlFor="protect-confirm" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                        {t('protectPdf.confirmLabel')}
                                    </label>
                                    <input
                                        id="protect-confirm"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => { setConfirmPassword(e.target.value); setResult(null); setError(null); }}
                                        placeholder={t('protectPdf.confirmPlaceholder')}
                                        className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                    />
                                </div>

                                <div className="space-y-2 rounded-xl border border-zinc-200 p-3 dark:border-zinc-700">
                                    <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('protectPdf.permissionsTitle')}</p>
                                    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                                        <input
                                            type="checkbox"
                                            checked={allowPrint}
                                            onChange={(e) => setAllowPrint(e.target.checked)}
                                            className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary/30 dark:border-zinc-600"
                                        />
                                        {t('protectPdf.allowPrint')}
                                    </label>
                                    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                                        <input
                                            type="checkbox"
                                            checked={allowCopy}
                                            onChange={(e) => setAllowCopy(e.target.checked)}
                                            className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary/30 dark:border-zinc-600"
                                        />
                                        {t('protectPdf.allowCopy')}
                                    </label>
                                </div>
                            </>
                        )}

                        {mode === 'unlock' && encrypted === false && (
                            <p className="text-xs text-amber-600 dark:text-amber-400">{t('protectPdf.notEncryptedHint')}</p>
                        )}
                        {mode === 'lock' && encrypted === true && (
                            <p className="text-xs text-amber-600 dark:text-amber-400">{t('protectPdf.alreadyEncryptedHint')}</p>
                        )}
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={t('protectPdf.processing')}
                    error={error}
                    successLabel={resultMeta}
                    downloadLabel={t('protectPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onEditAgain={result ? handleEditAgain : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? clearFile : undefined}
                    againLabel={t('protectPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={mode === 'lock' ? t('protectPdf.lock') : t('protectPdf.unlock')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!file && !processing}
                >
                    {file && !processing ? <PdfDocPreview source={result?.blob ?? file} /> : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
